import { Link } from 'react-router-dom';
import { applyFallbackImageOnError, FALLBACK_IMAGE_URL, getObraImageUrl } from '@/utils/image-utils';
import { generateWorkLink } from '@/utils/utils';

interface ObraCardProps {
    obra: {
        id: number;
        nome: string;
        slug?: string;
        imagem?: string | null;
        nota?: number | null;
        tipo?: string;
        capitulos?: number;
        total_capitulos?: number | null;
        capitulos_count?: number | null;
    };
    showRating?: boolean;
    aspectRatio?: 'portrait' | 'square';
    readingProgress?: {
        total: number;
        read: number;
        lastChapter?: string | number;
    };
    size?: 'normal' | 'small';
    onClick?: (e: React.MouseEvent) => void;
    /** `eager` evita falhas de lazy-load em carris/scroll horizontal (ex.: home). */
    coverLoading?: 'eager' | 'lazy';
}

export default function ObraCard({
    obra,
    aspectRatio = 'portrait',
    readingProgress,
    size = 'normal',
    onClick,
    coverLoading = 'lazy',
}: ObraCardProps) {
    const isSmall = size === 'small';
    const imgWidth = isSmall ? 260 : 360;
    const imgUrl = getObraImageUrl(obra.id, obra.imagem, imgWidth) || obra.imagem || FALLBACK_IMAGE_URL;
    const capCountRaw = obra.total_capitulos ?? obra.capitulos_count ?? obra.capitulos ?? null;
    const capCount =
        typeof capCountRaw === 'number' && Number.isFinite(capCountRaw) ? Math.max(0, Math.floor(capCountRaw)) : null;

    const handleClick = (e: React.MouseEvent) => {
        if (onClick) {
            e.preventDefault();
            onClick(e);
        }
    };

    return (
        <Link
            to={generateWorkLink(obra.id)}
            onClick={handleClick}
            className="group relative block h-full flex flex-col text-left no-underline"
        >
            <div
                className={`relative overflow-hidden rounded-xl bg-neutral-900 ${
                    aspectRatio === 'portrait' ? 'aspect-[2/3]' : 'aspect-square'
                } transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-xl group-hover:shadow-orange-500/10`}
            >
                <img
                    src={imgUrl}
                    alt={obra.nome}
                    onError={(e) => applyFallbackImageOnError(e.currentTarget)}
                    loading={coverLoading}
                    decoding="async"
                    {...(coverLoading === 'eager' ? {} : { fetchPriority: 'low' as const })}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />

                {capCount != null && (
                    <span className="absolute left-2 top-2 inline-flex items-center rounded-md border border-orange-300/35 bg-black/70 px-2 py-1 text-[10px] font-extrabold tracking-wide text-orange-200 backdrop-blur-sm">
                        {capCount} CAPS
                    </span>
                )}

                {obra.tipo && (
                    <div
                        className={`absolute top-2 right-2 bg-black/60 backdrop-blur-md rounded font-semibold uppercase tracking-wider text-white/90 border border-white/10 ${
                            isSmall ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'
                        }`}
                    >
                        {obra.tipo}
                    </div>
                )}

                {readingProgress && readingProgress.total > 0 && (
                    <div className="absolute bottom-0 left-0 w-full p-3 bg-gradient-to-t from-black/90 to-transparent">
                        <div className="flex justify-between items-center text-xs text-white/80 mb-1">
                            <span>Cap. {readingProgress.lastChapter || '?'}</span>
                            <span>{Math.round((readingProgress.read / readingProgress.total) * 100)}%</span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-1">
                            <div
                                className="bg-orange-500 h-1 rounded-full text-[0px]"
                                style={{ width: `${Math.min(100, (readingProgress.read / readingProgress.total) * 100)}%` }}
                            >
                                .
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className={`mt-2 flex-1 flex flex-col ${isSmall ? 'min-h-[2.5rem]' : 'min-h-[3rem]'}`}>
                <h3
                    className={`text-white font-bold leading-tight line-clamp-2 group-hover:text-orange-400 transition-colors ${
                        isSmall ? 'text-sm' : 'text-base'
                    }`}
                >
                    {obra.nome}
                </h3>
            </div>
        </Link>
    );
}
